import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { FEATURE_FLAGS } from '~/constants';
import { getDetailEmailFromCreator } from '~/core/utils/creator/detailedEmailTemplate';
import { getBasicEmailFromCreators } from '~/core/utils/creator/emailTemplate';
import { useClipboard } from '~/hooks/api/useClipboard';
import { useGetCreatorBenchmarks } from '~/hooks/api/useGetCreatorBenchmarks';
import { useGetCreatorInsights } from '~/hooks/api/useGetCreatorInsights';
import { useFeature } from '~/hooks/useFeatureFlag/useFeature';
import { selectTalentManager } from '~/selectors/talent-manager';
import { ICreator, ManagerType } from '~/types';

type UseCopyEmbedType = {
  isLoading: boolean;
  listId: string;
  savedListCreators: ICreator[];
  creators: ICreator[];
};

export const useCopyEmbed = ({
  isLoading,
  listId,
  savedListCreators,
  creators,
}: UseCopyEmbedType) => {
  const [shouldCopyBasic, setShouldCopyBasic] = useState(false);
  const [shouldCopyDetail, setShouldCopyDetail] = useState(false);

  const manager: ManagerType = useSelector(selectTalentManager);

  const { isEnabled: isFoamEnableAlignmentFFEnabled } = useFeature(
    FEATURE_FLAGS.foamEmbedsAlignment,
  );
  const { isEnabled: isFoamTalentLocationFFEnabled } = useFeature(
    FEATURE_FLAGS.foamTalentLocation,
  );

  const { copyToClipboard } = useClipboard('');

  const getInsights = useGetCreatorInsights();
  const getBenchmarks = useGetCreatorBenchmarks();

  // Handles the logic for creating the basic email template and copying it to the clipboard
  const handleCopyBasicEmbed = useCallback(
    (data: ICreator[]) => {
      const template = getBasicEmailFromCreators(
        data,
        isFoamEnableAlignmentFFEnabled,
        isFoamTalentLocationFFEnabled,
      );
      copyToClipboard(template);
    },
    [
      isFoamEnableAlignmentFFEnabled,
      isFoamTalentLocationFFEnabled,
      copyToClipboard,
    ],
  );

  // Handles the logic for creating the detailed email template and copying it to the clipboard
  const handleCopyDetailEmbed = useCallback(
    async (data: ICreator[]) => {
      try {
        const [insightsResponse, benchmarksResponse] = await Promise.all([
          getInsights.mutateAsync({
            managerId: manager.id,
            creatorIds: data.map((c) => c.id),
          }),
          getBenchmarks.mutateAsync({
            managerId: manager.id,
            creatorIds: data.map((c) => c.id),
          }),
        ]);

        if (!insightsResponse || !benchmarksResponse) {
          // Handle error here, like showing a toast
          return;
        }

        const benchmarks = benchmarksResponse.data;
        const insights = insightsResponse.data;

        const templates = data.map((creator) =>
          getDetailEmailFromCreator(
            creator,
            isFoamEnableAlignmentFFEnabled,
            isFoamTalentLocationFFEnabled,
            benchmarks,
            insights,
          ),
        );

        const template = templates.join('');
        copyToClipboard(template);
      } catch (error) {
        // Handle error if needed
      }
    },
    [
      manager.id,
      getInsights,
      getBenchmarks,
      isFoamEnableAlignmentFFEnabled,
      isFoamTalentLocationFFEnabled,
      copyToClipboard,
    ],
  );

  // Checks if loading is complete and if the user has triggered a copy basic embed while loading
  useEffect(() => {
    if (!isLoading && shouldCopyBasic) {
      const data = listId ? savedListCreators : creators;
      handleCopyBasicEmbed(data);
      setShouldCopyBasic(false);
    }
  }, [
    isLoading,
    shouldCopyBasic,
    savedListCreators,
    listId,
    creators,
    handleCopyBasicEmbed,
  ]);

  // Checks if loading is complete and if the user has triggered a copy detailed embed while loading
  useEffect(() => {
    if (!isLoading && shouldCopyDetail) {
      const data = listId ? savedListCreators : creators;
      handleCopyDetailEmbed(data);
      setShouldCopyDetail(false);
    }
  }, [
    isLoading,
    shouldCopyDetail,
    savedListCreators,
    listId,
    creators,
    handleCopyDetailEmbed,
  ]);

  // If isLoading is true, it sets the shouldCopyBasic. If not, it immediately calls handleCopyBasicEmbed.
  const triggerBasicEmbedCopy = useCallback(() => {
    if (isLoading) {
      setShouldCopyBasic(true);
    } else {
      const data = listId ? savedListCreators : creators;
      handleCopyBasicEmbed(data);
    }
  }, [isLoading, savedListCreators, listId, creators, handleCopyBasicEmbed]);

  // If isLoading is true, it sets the setShouldCopyDetail. If not, it immediately calls handleCopyDetailEmbed.
  const triggerDetailEmbedCopy = useCallback(() => {
    if (isLoading) {
      setShouldCopyDetail(true);
    } else {
      const data = listId ? savedListCreators : creators;
      handleCopyDetailEmbed(data);
    }
  }, [isLoading, savedListCreators, listId, creators, handleCopyDetailEmbed]);

  return {
    isBasicLoading: shouldCopyBasic,
    isDetailLoading:
      getInsights.isLoading || getBenchmarks.isLoading || shouldCopyDetail,
    triggerBasicEmbedCopy,
    triggerDetailEmbedCopy,
  };
};
